import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';

export interface CorporationLookup {
  id: string;
  name: string;
  short_name?: string;
  classification?: string;
}

interface RawCorporation {
  id: string;
  name?: string | null;
  short_name?: string | null;
  classification?: string | null;
  /** Manche Backends liefern stattdessen `corporation_type` */
  corporation_type?: string | null;
}

interface ListResponse {
  success: boolean;
  data: CorporationLookup[];
}

export interface UseCorporationsParams {
  search?: string;
  limit?: number;
  status?: 'active';
  /** Filter auf 'customer' | 'supplier' | 'internal'. BFF mappt das passend. */
  classification?: string;
}

/**
 * Hook gegen das BFF (`/api/v1/corporations`). Das BFF proxiet zur Corporation-API
 * und projiziert auf Lookup-Felder inkl. classification.
 */
export function useCorporations(params?: UseCorporationsParams) {
  return useQuery<ListResponse>({
    queryKey: ['lookups', 'corporations', params],
    queryFn: async () => {
      const query: Record<string, unknown> = {};
      if (params?.search) query.search = params.search;
      if (params?.limit !== undefined) query.limit = params.limit;
      if (params?.status) query.status = params.status;
      if (params?.classification) query.classification = params.classification;
      try {
        const resp = await apiClient.get<RawCorporation[]>('/api/v1/corporations', query);
        return {
          success: resp.success,
          data: (resp.data ?? []).map((c) => ({
            id: c.id,
            name: c.name ?? '—',
            short_name: c.short_name ?? undefined,
            classification: c.classification ?? c.corporation_type ?? undefined,
          })),
        };
      } catch {
        return { success: false, data: [] };
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}
